import { computed, onMounted, Ref } from "vue";
import { useStore } from "vuex";
import provideHook from "@/lib/provideHook";

import { fullName } from "@/mixins";
import { AppointmentServiceCategory } from "@/typings/appointment";
import { Technician } from "@/typings/technician";

export const useAppointmentSchedulingFields = provideHook(
  (
    formData: Ref<{
      service_category_id: number | null;
      technician_id: number | null;
    }>,
    allTechs: Ref<boolean>
  ) => {
    const store = useStore();

    onMounted(() => {
      fetchServiceCategories();
      fetchTechnicians();
    });

    const fetchServiceCategories = () => {
      if (store.state.serviceCategories.length === 0) {
        store.dispatch("fetchServiceCategories");
      }
    };

    const serviceCategoryOptions = computed(() =>
      store.state.serviceCategories
        .filter((sc: AppointmentServiceCategory) => !sc.archived)
        .map((sc: { id: number; name: string }) => ({
          label: sc.name,
          value: sc.id
        }))
    );

    const fetchTechnicians = () => {
      if (store.state.technicians.length === 0) {
        store.dispatch("fetchTechnicians");
      }
    };

    const technicianOptions = computed(() => {
      let technicians: Technician[] = store.state.technicians;
      if (!allTechs.value && formData.value.service_category_id) {
        technicians = technicians.filter((tech: Technician) =>
          tech.service_categories_ids.includes(
            formData.value.service_category_id
          )
        );
      }
      return technicians.map((tech) => ({
        ...tech,
        label: fullName(tech),
        value: tech.user_id
      }));
    });

    const selectedServiceCategory = computed<
      AppointmentServiceCategory | undefined
    >(() => {
      if (!formData.value.service_category_id) return undefined;
      return store.state.serviceCategories.find(
        (sc: AppointmentServiceCategory) =>
          sc.id === formData.value.service_category_id
      );
    });
    const selectedTech = computed<Technician | undefined>(() => {
      if (!formData.value.technician_id) return undefined;
      return store.state.technicians.find(
        (tech: Technician) => tech.user_id === formData.value.technician_id
      );
    });

    return {
      fetchServiceCategories,
      serviceCategoryOptions,
      fetchTechnicians,
      technicianOptions,
      selectedServiceCategory,
      selectedTech
    };
  }
);
